import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const tabs = [
  { key: "events", label: "Events", icon: "bi-calendar-event" },
  { key: "teams", label: "Sports Teams", icon: "bi-trophy" },
  { key: "fixtures", label: "Fixtures & Results", icon: "bi-stopwatch" },
  { key: "participants", label: "Participants", icon: "bi-people" },
  { key: "categories", label: "Categories", icon: "bi-tags" },
];

const emptyCategory = {
  id: null,
  name: "",
  code: "",
  category_type: "SCHOOL_EVENT",
  description: "",
  is_active: true,
};

const emptyEvent = {
  id: null,
  title: "",
  category: "",
  event_type: "SCHOOL_EVENT",
  audience: "WHOLE_SCHOOL",
  academic_year: "",
  term: "",
  class_groups: [],
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  location: "",
  description: "",
  organizer: "",
  requires_consent: false,
  transport_required: false,
  cost: "0.00",
  currency: "USD",
  status: "PLANNED",
  notes: "",
  publish_to_dashboard: false,
  publish_to_email: false,
  announcement_title: "",
  announcement_message: "",
  announcement_priority: "NORMAL",
};

const emptyTeam = {
  id: null,
  name: "",
  sport: "",
  class_group: "",
  coach: "",
  gender: "MIXED",
  season: "",
  description: "",
  is_active: true,
};

const emptyFixture = {
  id: null,
  team: "",
  event: "",
  opponent: "",
  fixture_type: "FRIENDLY",
  fixture_date: "",
  fixture_time: "",
  venue: "",
  home_away: "HOME",
  result_status: "SCHEDULED",
  school_score: "",
  opponent_score: "",
  match_report: "",
  publish_to_dashboard: false,
  publish_to_email: false,
  announcement_title: "",
  announcement_message: "",
  announcement_priority: "NORMAL",
};

const emptyParticipant = {
  id: null,
  event: "",
  fixture: "",
  student: "",
  status: "INVITED",
  consent_status: "NOT_REQUIRED",
  notes: "",
};

function EventsSports() {
  const [activeTab, setActiveTab] = useState("events");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [teamForm, setTeamForm] = useState(emptyTeam);
  const [fixtureForm, setFixtureForm] = useState(emptyFixture);
  const [eventFile, setEventFile] = useState(null);
  const [fixtureMedia, setFixtureMedia] = useState(null);
  const [participantForm, setParticipantForm] = useState(emptyParticipant);
  const [search, setSearch] = useState("");

  const visibleEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return events;
    return events.filter((item) =>
      [item.title, item.category_name, item.location, item.event_type_label, item.status]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [events, search]);

  const loadAll = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("events-sports/bootstrap/");
      const [summaryResponse, categoriesResponse, eventsResponse, teamsResponse, fixturesResponse, participantsResponse, optionsResponse] = await Promise.all([
        axiosInstance.get("events-sports/summary/"),
        axiosInstance.get("events-sports/categories/"),
        axiosInstance.get("events-sports/events/"),
        axiosInstance.get("events-sports/teams/"),
        axiosInstance.get("events-sports/fixtures/"),
        axiosInstance.get("events-sports/participants/"),
        axiosInstance.get("events-sports/options/"),
      ]);

      setSummary(summaryResponse.data || {});
      setCategories(categoriesResponse.data || []);
      setEvents(eventsResponse.data || []);
      setTeams(teamsResponse.data || []);
      setFixtures(fixturesResponse.data || []);
      setParticipants(participantsResponse.data || []);
      setStudents(optionsResponse.data?.students || []);
      setStaff(optionsResponse.data?.staff || []);
      setClassGroups(optionsResponse.data?.class_groups || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load events and sports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveCategory = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(categoryForm);
      if (categoryForm.id) {
        await axiosInstance.patch(`events-sports/categories/${categoryForm.id}/`, payload);
        toast.success("Category updated.");
      } else {
        await axiosInstance.post("events-sports/categories/", payload);
        toast.success("Category added.");
      }
      setCategoryForm(emptyCategory);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save category.");
    } finally {
      setLoading(false);
    }
  };

  const saveEvent = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const cleaned = cleanPayload(eventForm);
      if (!cleaned.end_date) cleaned.end_date = cleaned.start_date;
      const payload = new FormData();
      Object.entries(cleaned).forEach(([key, value]) => {
        if (key === "class_groups" && Array.isArray(value)) value.forEach((v) => payload.append("class_groups", v));
        else payload.append(key, value);
      });
      if (eventFile) payload.append("event_attachment", eventFile);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (eventForm.id) { await axiosInstance.patch(`events-sports/events/${eventForm.id}/`, payload, config); toast.success("Event updated."); }
      else { await axiosInstance.post("events-sports/events/", payload, config); toast.success("Event added."); }
      setEventForm(emptyEvent); setEventFile(null); await loadAll();
    } catch (error) { showError(error, "Failed to save event."); } finally { setLoading(false); }
  };

  const saveTeam = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(teamForm);
      if (teamForm.id) {
        await axiosInstance.patch(`events-sports/teams/${teamForm.id}/`, payload);
        toast.success("Team updated.");
      } else {
        await axiosInstance.post("events-sports/teams/", payload);
        toast.success("Team added.");
      }
      setTeamForm(emptyTeam);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save team.");
    } finally {
      setLoading(false);
    }
  };

  const saveFixture = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const cleaned = cleanPayload(fixtureForm);
      const payload = new FormData();
      Object.entries(cleaned).forEach(([key, value]) => payload.append(key, value));
      if (fixtureMedia) payload.append("result_media", fixtureMedia);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (fixtureForm.id) { await axiosInstance.patch(`events-sports/fixtures/${fixtureForm.id}/`, payload, config); toast.success("Fixture updated."); }
      else { await axiosInstance.post("events-sports/fixtures/", payload, config); toast.success("Fixture added."); }
      setFixtureForm(emptyFixture); setFixtureMedia(null); await loadAll();
    } catch (error) { showError(error, "Failed to save fixture."); } finally { setLoading(false); }
  };

  const saveParticipant = async (event) => {
    event.preventDefault();
    if (!participantForm.event && !participantForm.fixture) {
      toast.warning("Choose an event or fixture.");
      return;
    }
    if (participantForm.event && participantForm.fixture) {
      toast.warning("Choose either an event or fixture, not both.");
      return;
    }
    try {
      setLoading(true);
      const payload = cleanPayload(participantForm);
      if (participantForm.id) {
        await axiosInstance.patch(`events-sports/participants/${participantForm.id}/`, payload);
        toast.success("Participant updated.");
      } else {
        await axiosInstance.post("events-sports/participants/", payload);
        toast.success("Participant added.");
      }
      setParticipantForm(emptyParticipant);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save participant.");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadAll();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Events & Sports</h5>
              <p className="text-muted mb-0">
                Manage school events, sports teams, fixtures, participants and consent tracking.
              </p>
            </div>
            <button className="btn btn-outline-success" onClick={loadAll} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>

          <div className="row g-2 mt-3">
            <Summary label="Events" value={summary.events || 0} />
            <Summary label="Planned" value={summary.planned_events || 0} />
            <Summary label="Teams" value={summary.teams || 0} />
            <Summary label="Fixtures" value={summary.fixtures || 0} />
            <Summary label="Participants" value={summary.participants || 0} />
            <Summary label="Pending Consent" value={summary.pending_consent || 0} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "events" && (
        <SetupCard title="School Events">
          <form className="row g-3 mb-4" onSubmit={saveEvent}>
            <TextField label="Event Title" value={eventForm.title} onChange={(value) => setEventForm((prev) => ({ ...prev, title: value }))} required />
            <SelectField label="Category" value={eventForm.category} onChange={(value) => setEventForm((prev) => ({ ...prev, category: value }))} options={categories.filter((item) => item.is_active).map((item) => [item.id, item.name])} required={false} />
            <SelectField label="Event Type" value={eventForm.event_type} onChange={(value) => setEventForm((prev) => ({ ...prev, event_type: value }))} options={eventTypeOptions} />
            <SelectField label="Audience" value={eventForm.audience} onChange={(value) => setEventForm((prev) => ({ ...prev, audience: value }))} options={audienceOptions} />
            <TextField label="Start Date" type="date" value={eventForm.start_date || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, start_date: value }))} required />
            <TextField label="End Date" type="date" value={eventForm.end_date || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, end_date: value }))} />
            <TextField label="Start Time" type="time" value={eventForm.start_time || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, start_time: value }))} />
            <TextField label="End Time" type="time" value={eventForm.end_time || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, end_time: value }))} />
            <TextField label="Location" value={eventForm.location || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, location: value }))} />
            <SelectField label="Organizer" value={eventForm.organizer || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, organizer: value }))} options={staff.map((item) => [item.id, `${item.full_name} (${item.user_type})`])} required={false} />
            <MultiSelectField label="Class Groups" value={eventForm.class_groups || []} onChange={(value) => setEventForm((prev) => ({ ...prev, class_groups: value }))} options={classGroups.map((item) => [item.id, item.academic_year_name ? `${item.academic_year_name} - ${item.display_name}` : item.display_name])} />
            <TextField label="Cost" type="number" value={eventForm.cost} onChange={(value) => setEventForm((prev) => ({ ...prev, cost: value }))} />
            <TextField label="Currency" value={eventForm.currency || "USD"} onChange={(value) => setEventForm((prev) => ({ ...prev, currency: value.toUpperCase() }))} />
            <SelectField label="Status" value={eventForm.status} onChange={(value) => setEventForm((prev) => ({ ...prev, status: value }))} options={eventStatusOptions} />
            <SwitchField label="Requires consent" checked={eventForm.requires_consent} onChange={(value) => setEventForm((prev) => ({ ...prev, requires_consent: value }))} />
            <SwitchField label="Transport required" checked={eventForm.transport_required} onChange={(value) => setEventForm((prev) => ({ ...prev, transport_required: value }))} />
            <SwitchField label="Show on dashboards" checked={eventForm.publish_to_dashboard} onChange={(value) => setEventForm((prev) => ({ ...prev, publish_to_dashboard: value }))} />
            <SwitchField label="Prepare email notice" checked={eventForm.publish_to_email} onChange={(value) => setEventForm((prev) => ({ ...prev, publish_to_email: value }))} />
            <SelectField label="Notice Priority" value={eventForm.announcement_priority} onChange={(value) => setEventForm((prev) => ({ ...prev, announcement_priority: value }))} options={announcementPriorityOptions} />
            <TextField label="Notice Title" value={eventForm.announcement_title || ""} onChange={(value) => setEventForm((prev) => ({ ...prev, announcement_title: value }))} placeholder="Optional dashboard/email title" />
            <div className="col-12">
              <label className="form-label fw-semibold">Notice Message</label>
              <textarea className="form-control" rows="2" value={eventForm.announcement_message || ""} onChange={(event) => setEventForm((prev) => ({ ...prev, announcement_message: event.target.value }))} placeholder="Optional message for dashboard/email notices"></textarea>
            </div>
            <div className="col-md-6"><label className="form-label fw-semibold">Event PDF / Image / Word</label><input type="file" className="form-control" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={(e)=>setEventFile(e.target.files?.[0]||null)} /></div>
            <div className="col-12">
              <label className="form-label fw-semibold">Description</label>
              <textarea className="form-control" rows="2" value={eventForm.description || ""} onChange={(event) => setEventForm((prev) => ({ ...prev, description: event.target.value }))}></textarea>
            </div>
            <SaveBar loading={loading} label={eventForm.id ? "Update Event" : "Add Event"} onCancel={eventForm.id ? () => setEventForm(emptyEvent) : null} />
          </form>

          <div className="mb-3">
            <input className="form-control" placeholder="Search events..." value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>

          <SimpleTable
            headers={["Event", "Type", "Date", "Location", "Audience", "Attachment", "Notice", "Consent", "Status", "Actions"]}
            rows={visibleEvents.map((item) => [
              <div><div className="fw-semibold">{item.title}</div><small className="text-muted">{item.category_name || "No category"}</small></div>,
              item.event_type_label || item.event_type,
              <div><div>{item.start_date}</div><small className="text-muted">{item.start_time || ""}{item.end_time ? ` - ${item.end_time}` : ""}</small></div>,
              item.location || "-",
              item.audience_label || item.audience,
              item.event_attachment_url ? <a href={item.event_attachment_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-success">Open</a> : "-",
              <NoticeBadges item={item} />,
              item.requires_consent ? <span className="badge text-bg-warning">Required</span> : <span className="badge text-bg-light border">Not required</span>,
              <Status value={item.status} />,
              <ActionButtons onEdit={() => setEventForm(normalizeEvent(item))} onDelete={() => deleteRecord(`events-sports/events/${item.id}/`, item.title)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "teams" && (
        <SetupCard title="Sports Teams">
          <form className="row g-3 mb-4" onSubmit={saveTeam}>
            <TextField label="Team Name" value={teamForm.name} onChange={(value) => setTeamForm((prev) => ({ ...prev, name: value }))} required />
            <TextField label="Sport" value={teamForm.sport} onChange={(value) => setTeamForm((prev) => ({ ...prev, sport: value }))} placeholder="Football, Netball, Athletics" required />
            <SelectField label="Class Group" value={teamForm.class_group || ""} onChange={(value) => setTeamForm((prev) => ({ ...prev, class_group: value }))} options={classGroups.map((item) => [item.id, item.display_name])} required={false} />
            <SelectField label="Coach" value={teamForm.coach || ""} onChange={(value) => setTeamForm((prev) => ({ ...prev, coach: value }))} options={staff.map((item) => [item.id, item.full_name])} required={false} />
            <SelectField label="Gender" value={teamForm.gender} onChange={(value) => setTeamForm((prev) => ({ ...prev, gender: value }))} options={[["MIXED", "Mixed"], ["BOYS", "Boys"], ["GIRLS", "Girls"]]} />
            <TextField label="Season" value={teamForm.season || ""} onChange={(value) => setTeamForm((prev) => ({ ...prev, season: value }))} placeholder="e.g. 2026 Term 2" />
            <SwitchField label="Active" checked={teamForm.is_active} onChange={(value) => setTeamForm((prev) => ({ ...prev, is_active: value }))} />
            <div className="col-12"><input className="form-control" placeholder="Description" value={teamForm.description || ""} onChange={(event) => setTeamForm((prev) => ({ ...prev, description: event.target.value }))} /></div>
            <SaveBar loading={loading} label={teamForm.id ? "Update Team" : "Add Team"} onCancel={teamForm.id ? () => setTeamForm(emptyTeam) : null} />
          </form>

          <SimpleTable
            headers={["Team", "Sport", "Coach", "Class", "Gender", "Season", "Status", "Actions"]}
            rows={teams.map((item) => [
              item.name,
              item.sport,
              item.coach_name || "-",
              item.class_group_name || "-",
              item.gender_label || item.gender,
              item.season || "-",
              item.is_active ? <span className="badge text-bg-success">Active</span> : <span className="badge text-bg-secondary">Inactive</span>,
              <ActionButtons onEdit={() => setTeamForm(normalizeTeam(item))} onDelete={() => deleteRecord(`events-sports/teams/${item.id}/`, item.name)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "fixtures" && (
        <SetupCard title="Fixtures & Results">
          <form className="row g-3 mb-4" onSubmit={saveFixture}>
            <SelectField label="Team" value={fixtureForm.team} onChange={(value) => setFixtureForm((prev) => ({ ...prev, team: value }))} options={teams.map((item) => [item.id, `${item.name} - ${item.sport}`])} />
            <SelectField label="Linked Event" value={fixtureForm.event || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, event: value }))} options={events.map((item) => [item.id, item.title])} required={false} />
            <TextField label="Opponent" value={fixtureForm.opponent} onChange={(value) => setFixtureForm((prev) => ({ ...prev, opponent: value }))} required />
            <SelectField label="Fixture Type" value={fixtureForm.fixture_type} onChange={(value) => setFixtureForm((prev) => ({ ...prev, fixture_type: value }))} options={fixtureTypeOptions} />
            <TextField label="Fixture Date" type="date" value={fixtureForm.fixture_date || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, fixture_date: value }))} required />
            <TextField label="Time" type="time" value={fixtureForm.fixture_time || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, fixture_time: value }))} />
            <TextField label="Venue" value={fixtureForm.venue || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, venue: value }))} />
            <SelectField label="Home / Away" value={fixtureForm.home_away} onChange={(value) => setFixtureForm((prev) => ({ ...prev, home_away: value }))} options={[["HOME", "Home"], ["AWAY", "Away"], ["NEUTRAL", "Neutral"]]} />
            <SelectField label="Result Status" value={fixtureForm.result_status} onChange={(value) => setFixtureForm((prev) => ({ ...prev, result_status: value }))} options={resultStatusOptions} />
            <TextField label="School Score" type="number" value={fixtureForm.school_score || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, school_score: value }))} />
            <TextField label="Opponent Score" type="number" value={fixtureForm.opponent_score || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, opponent_score: value }))} />
            <SwitchField label="Show on dashboards" checked={fixtureForm.publish_to_dashboard} onChange={(value) => setFixtureForm((prev) => ({ ...prev, publish_to_dashboard: value }))} />
            <SwitchField label="Prepare email notice" checked={fixtureForm.publish_to_email} onChange={(value) => setFixtureForm((prev) => ({ ...prev, publish_to_email: value }))} />
            <SelectField label="Notice Priority" value={fixtureForm.announcement_priority} onChange={(value) => setFixtureForm((prev) => ({ ...prev, announcement_priority: value }))} options={announcementPriorityOptions} />
            <TextField label="Notice Title" value={fixtureForm.announcement_title || ""} onChange={(value) => setFixtureForm((prev) => ({ ...prev, announcement_title: value }))} placeholder="Optional dashboard/email title" />
            <div className="col-12">
              <label className="form-label fw-semibold">Notice Message</label>
              <textarea className="form-control" rows="2" value={fixtureForm.announcement_message || ""} onChange={(event) => setFixtureForm((prev) => ({ ...prev, announcement_message: event.target.value }))} placeholder="Optional message for dashboard/email notices"></textarea>
            </div>
            <div className="col-md-6"><label className="form-label fw-semibold">Result Image / Flyer</label><input type="file" className="form-control" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e)=>setFixtureMedia(e.target.files?.[0]||null)} /></div>
            <div className="col-12"><input className="form-control" placeholder="Match report / notes" value={fixtureForm.match_report || ""} onChange={(event) => setFixtureForm((prev) => ({ ...prev, match_report: event.target.value }))} /></div>
            <SaveBar loading={loading} label={fixtureForm.id ? "Update Fixture" : "Add Fixture"} onCancel={fixtureForm.id ? () => setFixtureForm(emptyFixture) : null} />
          </form>

          <SimpleTable
            headers={["Team", "Opponent", "Date", "Venue", "Type", "Result", "Flyer / Image", "Notice", "Status", "Actions"]}
            rows={fixtures.map((item) => [
              <div><div className="fw-semibold">{item.team_name}</div><small className="text-muted">{item.sport}</small></div>,
              item.opponent,
              item.fixture_date,
              item.venue || "-",
              item.fixture_type_label || item.fixture_type,
              item.school_score !== null && item.school_score !== undefined ? `${item.school_score} - ${item.opponent_score}` : "-",
              item.result_media_url ? <a href={item.result_media_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">View</a> : "-",
              <NoticeBadges item={item} />,
              <Status value={item.result_status} />,
              <ActionButtons onEdit={() => setFixtureForm(normalizeFixture(item))} onDelete={() => deleteRecord(`events-sports/fixtures/${item.id}/`, `${item.team_name} vs ${item.opponent}`)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "participants" && (
        <SetupCard title="Participants & Consent">
          <form className="row g-3 mb-4" onSubmit={saveParticipant}>
            <SelectField label="Event" value={participantForm.event || ""} onChange={(value) => setParticipantForm((prev) => ({ ...prev, event: value, fixture: value ? "" : prev.fixture }))} options={events.map((item) => [item.id, item.title])} required={false} />
            <SelectField label="Fixture" value={participantForm.fixture || ""} onChange={(value) => setParticipantForm((prev) => ({ ...prev, fixture: value, event: value ? "" : prev.event }))} options={fixtures.map((item) => [item.id, `${item.team_name} vs ${item.opponent}`])} required={false} />
            <SelectField label="Student" value={participantForm.student} onChange={(value) => setParticipantForm((prev) => ({ ...prev, student: value }))} options={students.map((item) => [item.id, `${item.full_name} (${item.admission_number || "No admission no."})`])} />
            <SelectField label="Participation Status" value={participantForm.status} onChange={(value) => setParticipantForm((prev) => ({ ...prev, status: value }))} options={participantStatusOptions} />
            <SelectField label="Consent Status" value={participantForm.consent_status} onChange={(value) => setParticipantForm((prev) => ({ ...prev, consent_status: value }))} options={consentStatusOptions} />
            <div className="col-12"><input className="form-control" placeholder="Notes" value={participantForm.notes || ""} onChange={(event) => setParticipantForm((prev) => ({ ...prev, notes: event.target.value }))} /></div>
            <SaveBar loading={loading} label={participantForm.id ? "Update Participant" : "Add Participant"} onCancel={participantForm.id ? () => setParticipantForm(emptyParticipant) : null} />
          </form>

          <SimpleTable
            headers={["Student", "Event / Fixture", "Class", "Status", "Consent", "Notes", "Actions"]}
            rows={participants.map((item) => [
              <div><div className="fw-semibold">{item.student_name}</div><small className="text-muted">{item.student_number || "-"}</small></div>,
              item.event_title || item.fixture_name || "-",
              item.class_group_name || "-",
              <Status value={item.status} />,
              <Status value={item.consent_status} />,
              item.notes || "-",
              <ActionButtons onEdit={() => setParticipantForm(normalizeParticipant(item))} onDelete={() => deleteRecord(`events-sports/participants/${item.id}/`, item.student_name)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "categories" && (
        <SetupCard title="Event & Sport Categories">
          <form className="row g-3 mb-4" onSubmit={saveCategory}>
            <TextField label="Category Name" value={categoryForm.name} onChange={(value) => setCategoryForm((prev) => ({ ...prev, name: value }))} required />
            <TextField label="Code" value={categoryForm.code} onChange={(value) => setCategoryForm((prev) => ({ ...prev, code: value.toUpperCase() }))} required />
            <SelectField label="Category Type" value={categoryForm.category_type} onChange={(value) => setCategoryForm((prev) => ({ ...prev, category_type: value }))} options={categoryTypeOptions} />
            <SwitchField label="Active" checked={categoryForm.is_active} onChange={(value) => setCategoryForm((prev) => ({ ...prev, is_active: value }))} />
            <div className="col-12"><input className="form-control" placeholder="Description" value={categoryForm.description || ""} onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))} /></div>
            <SaveBar loading={loading} label={categoryForm.id ? "Update Category" : "Add Category"} onCancel={categoryForm.id ? () => setCategoryForm(emptyCategory) : null} />
          </form>

          <SimpleTable
            headers={["Category", "Code", "Type", "Status", "Actions"]}
            rows={categories.map((item) => [
              item.name,
              item.code,
              item.category_type_label || item.category_type,
              item.is_active ? <span className="badge text-bg-success">Active</span> : <span className="badge text-bg-secondary">Inactive</span>,
              <ActionButtons onEdit={() => setCategoryForm(normalizeCategory(item))} onDelete={() => deleteRecord(`events-sports/categories/${item.id}/`, item.name)} />,
            ])}
          />
        </SetupCard>
      )}
    </div>
  );
}

const categoryTypeOptions = [["SCHOOL_EVENT", "School Event"], ["SPORT", "Sport"], ["CLUB", "Club"], ["TRIP", "Trip / Educational Visit"], ["MEETING", "Meeting"], ["OTHER", "Other"]];
const eventTypeOptions = [["SCHOOL_EVENT", "School Event"], ["SPORTS_EVENT", "Sports Event"], ["ASSEMBLY", "Assembly"], ["CLUB", "Club Activity"], ["TRIP", "Trip / Educational Visit"], ["MEETING", "Meeting"], ["CEREMONY", "Ceremony"], ["OTHER", "Other"]];
const audienceOptions = [["WHOLE_SCHOOL", "Whole School"], ["CLASS_GROUPS", "Selected Class Groups"], ["STAFF", "Staff"], ["PARENTS", "Parents / Guardians"], ["SELECTED_STUDENTS", "Selected Students"], ["PUBLIC", "Public"]];
const eventStatusOptions = [["DRAFT", "Draft"], ["PLANNED", "Planned"], ["ONGOING", "Ongoing"], ["COMPLETED", "Completed"], ["CANCELLED", "Cancelled"]];
const fixtureTypeOptions = [["FRIENDLY", "Friendly"], ["LEAGUE", "League"], ["TOURNAMENT", "Tournament"], ["INTER_HOUSE", "Inter-house"], ["OTHER", "Other"]];
const announcementPriorityOptions = [["NORMAL", "Normal"], ["IMPORTANT", "Important"], ["URGENT", "Urgent"]];

const resultStatusOptions = [["SCHEDULED", "Scheduled"], ["PLAYED", "Played"], ["POSTPONED", "Postponed"], ["CANCELLED", "Cancelled"]];
const participantStatusOptions = [["INVITED", "Invited"], ["CONFIRMED", "Confirmed"], ["ATTENDED", "Attended"], ["ABSENT", "Absent"], ["EXCUSED", "Excused"]];
const consentStatusOptions = [["NOT_REQUIRED", "Not Required"], ["PENDING", "Pending"], ["GRANTED", "Granted"], ["DECLINED", "Declined"]];

function cleanPayload(form) {
  const payload = {};
  Object.entries(form).forEach(([key, value]) => {
    if (key === "id") return;
    if (Array.isArray(value)) {
      payload[key] = value;
      return;
    }
    if (value !== "" && value !== null && value !== undefined) payload[key] = value;
  });
  return payload;
}

function showError(error, fallback) {
  const data = error.response?.data;
  const message =
    data?.non_field_errors?.[0] ||
    data?.detail ||
    Object.values(data || {})?.flat?.()?.[0] ||
    fallback;
  toast.error(message);
}

function normalizeCategory(item) {
  return { id: item.id, name: item.name || "", code: item.code || "", category_type: item.category_type || "SCHOOL_EVENT", description: item.description || "", is_active: Boolean(item.is_active) };
}

function normalizeEvent(item) {
  return {
    ...emptyEvent,
    id: item.id,
    title: item.title || "",
    category: item.category || "",
    event_type: item.event_type || "SCHOOL_EVENT",
    audience: item.audience || "WHOLE_SCHOOL",
    academic_year: item.academic_year || "",
    term: item.term || "",
    class_groups: item.class_groups || [],
    start_date: item.start_date || "",
    end_date: item.end_date || "",
    start_time: item.start_time || "",
    end_time: item.end_time || "",
    location: item.location || "",
    description: item.description || "",
    organizer: item.organizer || "",
    requires_consent: Boolean(item.requires_consent),
    transport_required: Boolean(item.transport_required),
    cost: item.cost ?? "0.00",
    currency: item.currency || "USD",
    status: item.status || "PLANNED",
    notes: item.notes || "",
    publish_to_dashboard: Boolean(item.publish_to_dashboard),
    publish_to_email: Boolean(item.publish_to_email),
    announcement_title: item.announcement_title || "",
    announcement_message: item.announcement_message || "",
    announcement_priority: item.announcement_priority || "NORMAL",
  };
}

function normalizeTeam(item) {
  return { ...emptyTeam, id: item.id, name: item.name || "", sport: item.sport || "", class_group: item.class_group || "", coach: item.coach || "", gender: item.gender || "MIXED", season: item.season || "", description: item.description || "", is_active: Boolean(item.is_active) };
}

function normalizeFixture(item) {
  return {
    ...emptyFixture,
    id: item.id,
    team: item.team || "",
    event: item.event || "",
    opponent: item.opponent || "",
    fixture_type: item.fixture_type || "FRIENDLY",
    fixture_date: item.fixture_date || "",
    fixture_time: item.fixture_time || "",
    venue: item.venue || "",
    home_away: item.home_away || "HOME",
    result_status: item.result_status || "SCHEDULED",
    school_score: item.school_score ?? "",
    opponent_score: item.opponent_score ?? "",
    match_report: item.match_report || "",
    publish_to_dashboard: Boolean(item.publish_to_dashboard),
    publish_to_email: Boolean(item.publish_to_email),
    announcement_title: item.announcement_title || "",
    announcement_message: item.announcement_message || "",
    announcement_priority: item.announcement_priority || "NORMAL",
  };
}

function normalizeParticipant(item) {
  return { ...emptyParticipant, id: item.id, event: item.event || "", fixture: item.fixture || "", student: item.student || "", status: item.status || "INVITED", consent_status: item.consent_status || "NOT_REQUIRED", notes: item.notes || "" };
}

function NoticeBadges({ item }) {
  if (!item?.publish_to_dashboard && !item?.publish_to_email) {
    return <span className="badge text-bg-light border">Internal only</span>;
  }

  return (
    <div className="d-flex flex-wrap gap-1">
      {item.publish_to_dashboard && <span className="badge text-bg-success">Dashboard</span>}
      {item.publish_to_email && <span className="badge text-bg-info">Email</span>}
      {item.announcement_priority && item.announcement_priority !== "NORMAL" && (
        <span className="badge text-bg-warning">{item.announcement_priority}</span>
      )}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="col-6 col-lg-2">
      <div className="border rounded-3 p-3 bg-light h-100">
        <small className="text-muted fw-bold">{label}</small>
        <div className="fs-4 fw-bold" style={{ color: "#125b2d" }}>{value}</div>
      </div>
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h6 className="fw-bold mb-3">{title}</h6>
        {children}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder = "", required = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input type={type} className="form-control" value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} required={required} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, required = true }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}>
        <option value="">Select {label}</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </div>
  );
}

function MultiSelectField({ label, value, onChange, options }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select
        multiple
        className="form-select"
        value={(value || []).map(String)}
        onChange={(event) => onChange(Array.from(event.target.selectedOptions).map((option) => option.value))}
        style={{ minHeight: 95 }}
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
      <small className="text-muted">Hold Ctrl to select multiple.</small>
    </div>
  );
}

function SwitchField({ label, checked, onChange }) {
  return (
    <div className="col-md-4 d-flex align-items-end">
      <label className="form-check form-switch mb-2">
        <input className="form-check-input" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        <span className="form-check-label fw-semibold">{label}</span>
      </label>
    </div>
  );
}

function SaveBar({ loading, label, onCancel }) {
  return (
    <div className="col-12 d-flex gap-2">
      <button className="btn btn-success" disabled={loading} type="submit">
        <i className="bi bi-check2-circle me-2"></i>{label}
      </button>
      {onCancel && (
        <button type="button" className="btn btn-light border" onClick={onCancel}>Cancel</button>
      )}
    </div>
  );
}

function Status({ value }) {
  const normalized = String(value || "").toUpperCase();
  const className = ["COMPLETED", "PLAYED", "ATTENDED", "GRANTED", "CONFIRMED"].includes(normalized)
    ? "text-bg-success"
    : ["CANCELLED", "DECLINED", "ABSENT"].includes(normalized)
    ? "text-bg-danger"
    : ["PENDING", "ONGOING", "POSTPONED"].includes(normalized)
    ? "text-bg-warning"
    : "text-bg-light border";
  return <span className={`badge ${className}`}>{String(value || "-").replaceAll("_", " ")}</span>;
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="d-flex gap-1">
      <button type="button" className="btn btn-sm btn-light border" onClick={onEdit} title="Edit">
        <i className="bi bi-pencil-square text-success"></i>
      </button>
      <button type="button" className="btn btn-sm btn-light border" onClick={onDelete} title="Delete">
        <i className="bi bi-trash text-danger"></i>
      </button>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive border rounded-3">
      <table className="table table-sm align-middle mb-0">
        <thead className="table-light">
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records found.</td></tr>
          ) : (
            rows.map((row, rowIndex) => (
              <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default EventsSports;
